import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { Aviso, Botao, Chip } from "../ui";
import { chaveDoEvento, fraseDoEvento, useFilaAoVivo, type EventoDaFila } from "../../nucleo/filaAoVivo";

const MAXIMO_NA_TELA = 4;

/**
 * O que acontece na fila enquanto a tela está aberta: chamado novo, resposta do cliente, reabertura.
 *
 * Cada aviso chega pelo fluxo ao vivo da API (`GET /api/tickets/eventos`), faz a lista recarregar
 * sozinha e fica na tela até a pessoa dispensá-lo ou abrir o chamado. É região `status`: o leitor de
 * tela anuncia o que chegou sem tirar o foco de onde a pessoa estava.
 */
export function AvisosDaFila() {
  const cliente = useQueryClient();
  const [avisos, setAvisos] = useState<EventoDaFila[]>([]);
  const [conectado, setConectado] = useState(false);
  const vistos = useRef(new Set<string>());

  useFilaAoVivo(
    true,
    (evento) => {
      // A reconexão reentrega o que passou na janela: o que já foi avisado não avisa de novo.
      const chave = chaveDoEvento(evento);
      if (vistos.current.has(chave)) return;
      vistos.current.add(chave);
      void cliente.invalidateQueries({ queryKey: ["tickets"] });
      setAvisos((atuais) => [evento, ...atuais].slice(0, MAXIMO_NA_TELA));
    },
    setConectado,
  );

  return (
    <div data-testid="fila-ao-vivo" data-ao-vivo={conectado ? "sim" : "nao"}>
      <p className="nota-rodape mb-2">
        <Chip estado={conectado ? "ok" : "aguardando"}>{conectado ? "Fila ao vivo" : "Conectando a fila ao vivo…"}</Chip>
      </p>
      <div role="status" aria-live="polite" data-testid="avisos-da-fila">
        {avisos.map((aviso) => (
          <Aviso key={`${aviso.tipo}-${aviso.ticket_id}-${aviso.ocorrido_em}`} tom="marca" className="mb-2" data-testid="aviso-da-fila">
            {fraseDoEvento(aviso)} <Link to={`/admin/chamados/${aviso.ticket_id}`}>Abrir chamado</Link>{" "}
            <Botao
              type="button"
              variante="fantasma"
              tamanho="pequeno"
              aria-label={`Dispensar o aviso do chamado ${aviso.numero}`}
              onClick={() => setAvisos((atuais) => atuais.filter((item) => item !== aviso))}
            >
              Dispensar
            </Botao>
          </Aviso>
        ))}
      </div>
    </div>
  );
}
